import type { BookingProposalItemRecord, BookingProposalItemRecordList, MoneyRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { SelectedSalesItemsCarGroupedWithQuantity } from 'noddi-types';
import { NoddiCircularLoader } from 'noddi-ui-web';
import { catalogPriceMapKey, type ProposalCatalogPrice } from 'noddi-util';
import { useProposalBookableServices } from '../../hooks/useProposalBookableServices';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { ApiErrorMessageWithTrans } from '../Error/ApiErrorMessageWithTrans';
import { ProposalShoppingCartSummary } from './ProposalShoppingCartSummary';

type Props = {
  userGroupId: number;
  bookingProposalItems: BookingProposalItemRecord[] | BookingProposalItemRecordList[];
  wrapCarsInCard?: boolean;
  serviceDepartmentId: number;
  /** EonTyre / price-list discounts matched onto proposal lines. */
  catalogPrices?: ProposalCatalogPrice[];
};

const buildAdHocPriceMap = (items: BookingProposalItemRecord[] | BookingProposalItemRecordList[]) => {
  const map = new Map<string, MoneyRecord>();
  for (const item of items) {
    for (const { salesItem, priceAdHoc } of item.salesItemsWithQuantity) {
      if (priceAdHoc) {
        map.set(catalogPriceMapKey(item.car.licensePlate.number, salesItem.id), priceAdHoc.priceGross);
      }
    }
  }
  return map;
};

const buildCatalogPriceMap = (catalogPrices: ProposalCatalogPrice[]) => {
  const map = new Map<string, ProposalCatalogPrice>();
  for (const price of catalogPrices) {
    map.set(catalogPriceMapKey(price.licensePlateNumber, price.salesItemId), price);
  }
  return map;
};

// todo, add delivery window param to the shopping cart, if algo selects delivery window with price, it should be included...
export const ProposalShoppingCart = ({
  userGroupId,
  bookingProposalItems,
  wrapCarsInCard,
  serviceDepartmentId,
  catalogPrices = []
}: Props) => {
  const { currentBrand } = useCurrentBrand();

  const carsWithSalesItems: SelectedSalesItemsCarGroupedWithQuantity[] = bookingProposalItems.map((item) => {
    const selectedSalesItems = item.salesItemsWithQuantity.map(({ salesItem, quantity }) => ({
      salesItemId: salesItem.id,
      quantity
    }));

    return {
      carId: item.car.id,
      selectedSalesItems
    };
  });

  const adHocPrices = buildAdHocPriceMap(bookingProposalItems);
  const catalogPriceByKey = buildCatalogPriceMap(catalogPrices);
  const { openOrder } = useProposalBookableServices({
    serviceDepartmentId,
    licensePlateNumber: bookingProposalItems[0]?.car.licensePlate.number
  });

  const { data, isPending, error } = noddiAsync.usePostAsGetQuery({
    type: URLKeys.postGetShoppingCartForNewBookingWithoutAddress,
    input: {
      serviceDepartmentId,
      carsWithSalesItems,
      userGroupId,
      brandDomain: currentBrand
    }
  });

  if (isPending) {
    return <NoddiCircularLoader />;
  }

  if (error) {
    return <ApiErrorMessageWithTrans error={error} />;
  }

  return (
    <ProposalShoppingCartSummary
      userGroupId={userGroupId}
      wrapCarsInCard={wrapCarsInCard}
      shoppingCart={data.data}
      openOrder={openOrder}
      adHocPrices={adHocPrices}
      catalogPriceByKey={catalogPriceByKey}
    />
  );
};
